import { Button, Divider, Flex, Form, FormSelection } from '@shared/components';
import { zodResolver } from '@shared/utils';
import { UserAppRoleSelection } from '@user-management/role-management';
import { USER_STATUS_TEXT, UserStatus } from '@user-management/user-details';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useUserManagementListSearchParams } from '../../hooks';

const UserFilterFormSchema = z.object({
  role: z.object({ label: z.string(), value: z.string() }).optional().nullable(),
  status: z.nativeEnum(UserStatus).nullable(),
});

type UserFilterFormInputs = z.infer<typeof UserFilterFormSchema>;

function getStatusOptions() {
  return Object.values(UserStatus).map((status) => {
    return { label: USER_STATUS_TEXT[status], value: status };
  });
}

export default function UserFilterForm() {
  const [{ role, status }, { changeRoleAndStatus }] = useUserManagementListSearchParams();
  const statusOptions = useMemo(() => getStatusOptions(), []);

  const formContext = useForm<UserFilterFormInputs>({
    defaultValues: {
      role: { label: role, value: role },
      status: status,
    },
    resolver: zodResolver(UserFilterFormSchema),
  });

  const handleSubmit = (formValues: UserFilterFormInputs) => {
    const { role, status } = formValues;
    changeRoleAndStatus(role?.value ?? '', status ?? null);
  };

  const handleReset = () => {
    formContext.reset({
      role: { label: '', value: '' },
      status: null,
    });
    changeRoleAndStatus('', null);
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleSubmit)} onReset={handleReset}>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            width: '100%',
            gap: theme.spacing(4),
            border: '1px solid',
            borderColor: theme.palette.grey[200],
            borderRadius: theme.shape.rounded(2),
            backgroundColor: theme.palette.background.paper,
            p: theme.spacing(4),
          })}
        >
          <Flex sx={{ gap: (theme) => theme.spacing(4), alignItems: 'center' }}>
            <UserAppRoleSelection
              name='role'
              label='Role'
              placeholder='Select Role'
              multiple={undefined}
              sx={{ maxWidth: '347px' }}
            />
            <FormSelection
              label='Status'
              name='status'
              placeholder='Select Status'
              options={statusOptions}
              sx={{ maxWidth: '347px', width: '100%' }}
            />
          </Flex>
          <Divider />
          <Flex sx={{ justifyContent: 'end', gap: (theme) => theme.spacing(2) }}>
            <Button color='secondary' variant='outlined' type='reset'>
              Reset
            </Button>
            <Button type='submit'>Apply</Button>
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
}
